import { List, Map } from 'immutable';

import { EBAY, ETSY, FAIRE, SHOPIFY } from './channels';
import { ATTRIBUTE } from './attributes';
import { EMPTY } from '.';

export const NEW = 'new';

export const ENABLED = 'enabled';

export const ERROR = 'error';

export const OVERSET = 'overset';

export const REQUIRED = 'required';

export const VALUE = 'value';

export const SIZE_UNIT = {
  CM: 'cm',
  FT: 'ft',
  IN: 'in',
  M: 'm',
  MM: 'mm',
  YD: 'yd',
};

export const WEIGHT_UNIT = {
  LB: 'lb',
  OZ: 'oz',
  KG: 'kg',
  G: 'g',
};

export const WEIGHT_UNITS = [WEIGHT_UNIT.LB, WEIGHT_UNIT.OZ, WEIGHT_UNIT.KG, WEIGHT_UNIT.G];
export const SIZE_UNITS = [SIZE_UNIT.IN, SIZE_UNIT.CM];

export const AMOUNT = {
  MINUS_ONE: '-1',
  MINUS_TWO: '-2',
  ONE: '1',
  ONE_CENT: '0.01',
  ZERO: '0',
  ZERO_CENT: '0.00',
};

export const CURRENCY = {
  AUD: 'AUD',
  CAD: 'CAD',
  EUR: 'EUR',
  GBP: 'GBP',
  USD: 'USD',
};

export const CURRENCY_SIGN = {
  [CURRENCY.AUD]: '$',
  [CURRENCY.CAD]: '$',
  [CURRENCY.EUR]: '€',
  [CURRENCY.GBP]: '£',
  [CURRENCY.USD]: '$',
};

export const CURRENCY_LOCATION = {
  [CURRENCY.AUD]: 'Australia',
  [CURRENCY.CAD]: 'Canada',
  [CURRENCY.EUR]: 'European Union',
  [CURRENCY.GBP]: 'United Kingdom',
  [CURRENCY.USD]: 'United States',
};

export const CURRENCIES = {
  [FAIRE]: [CURRENCY.USD, CURRENCY.CAD, CURRENCY.GBP, CURRENCY.AUD, CURRENCY.EUR],
};

export const PRICE_API_KEY_TO_ATTRIBUTE_CURRENCY_MAP = {
  prices_retail_aud: { attribute: ATTRIBUTE.RETAIL_PRICE, currency: CURRENCY.AUD },
  prices_retail_cad: { attribute: ATTRIBUTE.RETAIL_PRICE, currency: CURRENCY.CAD },
  prices_retail_eur: { attribute: ATTRIBUTE.RETAIL_PRICE, currency: CURRENCY.EUR },
  prices_retail_gbp: { attribute: ATTRIBUTE.RETAIL_PRICE, currency: CURRENCY.GBP },
  prices_retail_usd: { attribute: ATTRIBUTE.RETAIL_PRICE, currency: CURRENCY.USD },
  prices_wholesale_aud: { attribute: ATTRIBUTE.WHOLESALE_PRICE, currency: CURRENCY.AUD },
  prices_wholesale_cad: { attribute: ATTRIBUTE.WHOLESALE_PRICE, currency: CURRENCY.CAD },
  prices_wholesale_eur: { attribute: ATTRIBUTE.WHOLESALE_PRICE, currency: CURRENCY.EUR },
  prices_wholesale_gbp: { attribute: ATTRIBUTE.WHOLESALE_PRICE, currency: CURRENCY.GBP },
  prices_wholesale_usd: { attribute: ATTRIBUTE.WHOLESALE_PRICE, currency: CURRENCY.USD },
};

export const PRICE_ATTRIBUTE_CURRENCY_TO_API_KEY_MAP = {
  [ATTRIBUTE.RETAIL_PRICE]: {
    [CURRENCY.AUD]: 'prices_retail_aud',
    [CURRENCY.CAD]: 'prices_retail_cad',
    [CURRENCY.EUR]: 'prices_retail_eur',
    [CURRENCY.GBP]: 'prices_retail_gbp',
    [CURRENCY.USD]: 'prices_retail_usd',
  },
  [ATTRIBUTE.WHOLESALE_PRICE]: {
    [CURRENCY.AUD]: 'prices_wholesale_aud',
    [CURRENCY.CAD]: 'prices_wholesale_cad',
    [CURRENCY.EUR]: 'prices_wholesale_eur',
    [CURRENCY.GBP]: 'prices_wholesale_gbp',
    [CURRENCY.USD]: 'prices_wholesale_usd',
  },
};

export const SELLING_METHOD = {
  CASE: 'By the case',
  ITEM: 'By the item',
  SIZE: 'Open sizing',
};

export const DEFAULT_VALUES = {
  DISTANCE_UNIT: Map({ [VALUE]: SIZE_UNIT.IN }),
  EMPTY_STRING: Map({ [VALUE]: '' }),
  EMPTY_LIST: Map({ [VALUE]: EMPTY.LIST }),
  EMPTY_MAP: Map({ [VALUE]: EMPTY.MAP }),
  EMPTY_ORDERED_MAP: Map({ [VALUE]: EMPTY.ORDERED_MAP }),
  EMPTY_ORDERED_SET: Map({ [VALUE]: EMPTY.ORDERED_SET }),
  EMPTY_SET: Map({ [VALUE]: EMPTY.SET }),
  EMPTY_INTERNATIONAL_PRICES: Map({
    [VALUE]: Map({
      [CURRENCY.AUD]: '',
      [CURRENCY.CAD]: '',
      [CURRENCY.EUR]: '',
      [CURRENCY.GBP]: '',
      [CURRENCY.USD]: '',
    }),
  }),
  FALSE: Map({ [VALUE]: false }),
  MASS_UNIT: Map({ [VALUE]: WEIGHT_UNIT.LB }),
  MINUS_ONE: Map({ [VALUE]: AMOUNT.MINUS_ONE }),
  MINUS_TWO: Map({ [VALUE]: AMOUNT.MINUS_TWO }),
  NULL: Map({ [VALUE]: null }),
  ONE: Map({ [VALUE]: AMOUNT.ONE }),
  ONE_CENT: Map({ [VALUE]: AMOUNT.ONE_CENT }),
  SELLING_METHOD: Map({ [VALUE]: SELLING_METHOD.ITEM }),
  TRUE: Map({ [VALUE]: true }),
  UNIT: Map({ [VALUE]: WEIGHT_UNIT.LB }),
  ZERO: Map({ [VALUE]: AMOUNT.ZERO }),
  ZERO_CENT: Map({ [VALUE]: AMOUNT.ZERO_CENT }),
};

export const ENDPOINTS = {
  CREATE: {
    [ETSY]: 'products/create',
    [EBAY]: 'product',
    [FAIRE]: 'product',
    [SHOPIFY]: 'product',
    LINKED: 'mergeNewProducts',
  },
};

export const OPERATIONS = {
  [EBAY]: {
    CONDITION_DESCRIPTION: 'condition_description.set',
    CONDITION_ID: 'condition_id.set',
    COUNTRY: 'country.set',
    DEPTH: 'shipping_package_depth.set',
    DESCRIPTION: 'description.set',
    IRREGULAR: 'shipping_package_irregular.set',
    ITEM_CATEGORY: 'category_id.set',
    ITEM_SPECIFICS: {
      SET: 'item_specifics.set',
      UPDATE: 'item_specifics.update',
    },
    KEYWORDS: 'keywords.set',
    LENGTH: 'shipping_package_length.set',
    LOCATION: 'location.set',
    PAYMENT_POLICY: 'payment_policy_id.set',
    PHOTOS: {
      ADD: 'photos.add',
      CHANGE_TO: 'photos.changeTo',
      DELETE: 'photos.delete',
      REPLACE: 'photos.replace',
      SWAP: 'photos.swap',
      UPDATE: 'photos.update',
    },
    POSTAL_CODE: 'postal_code.set',
    PRICE: 'price.set',
    PRODUCT_DETAILS: {
      SET: 'product_listing_details.set',
      UPDATE: 'product_listing_details.update',
    },
    QUANTITY: 'quantity.set',
    SKU: 'sku.set',
    RETURN_POLICY: 'return_policy_id.set',
    SCHEDULED_AT: 'scheduled_at.set',
    SHIPPING_POLICY: 'shipping_policy_id.set',
    STATUS: 'status.set',
    STORE_CATEGORY: 'custom_category_id.set',
    TITLE: 'title.set',
    VARIATIONS: 'variationsInventory.changeTo',
    VIDEOS: {
      ADD: 'videos.add',
      CHANGE_TO: 'videos.changeTo',
      DELETE: 'videos.delete',
      REPLACE: 'videos.replace',
    },
    WEIGHT_MAJOR: 'shipping_package_major_weight.set',
    WEIGHT_MINOR: 'shipping_package_minor_weight.set',
    WIDTH: 'shipping_package_width.set',
  },
  [ETSY]: {
    CATEGORY: 'taxonomy.set',
    DESCRIPTION: 'description.set',
    DIGITAL: 'isDigital.set',
    FILES: {
      ADD: 'files.add',
      CHANGE_TO: 'files.changeTo',
      DELETE: 'files.delete',
      SWAP: 'files.swap',
      REPLACE: 'files.replace',
    },
    HEIGHT: 'itemHeight.set',
    IS_SUPPLY: 'is_supply.set',
    LENGTH: 'itemLength.set',
    MATERIALS: {
      ADD: 'materials.add',
      CHANGE_TO: 'materials.changeTo',
      DELETE: 'materials.delete',
    },
    PERSONALIZATION: {
      IS_PERSONALIZABLE_SET: 'isPersonalizable.set',
      IS_REQUIRED_SET: 'personalizationIsRequired.set',
      CHAR_COUNT_MAX_SET: 'personalizationCharCountMax.set',
      INSTRUCTIONS_SET: 'personalizationInstructions.set',
    },
    PHOTOS: {
      ADD: 'photos.add',
      CHANGE_TO: 'photos.changeTo',
      DELETE: 'photos.delete',
      REPLACE: 'photos.replace',
      SWAP: 'photos.swap',
      UPDATE: 'photos.update',
    },
    PRICE: 'priceInventory.changeTo',
    PROCESSING_PROFILE: 'readinessStateIdInventory.changeTo',
    PRODUCTION_PARTNERS: 'productionPartners.set',
    QUANTITY: 'quantityInventory.changeTo',
    RETURN_POLICY: 'return_policy_id.set',
    SCHEDULED_AT: 'scheduled_at.set',
    SKU: 'skuInventory.changeTo',
    SECTION: 'section.set',
    SHIPPING_PROFILE: 'shipping_template_id.set',
    SHOULD_AUTO_RENEW: 'should_auto_renew.set',
    STATUS: 'status.set',
    TAGS: {
      ADD: 'tags.add',
      DELETE: 'tags.delete',
      CHANGE_TO: 'tags.changeTo',
    },
    TITLE: 'title.set',
    TAXONOMY_ATTRIBUTES: 'attributes.set',
    VARIATIONS: 'variationsInventory.changeTo',
    VIDEOS: {
      ADD: 'videos.add',
      CHANGE_TO: 'videos.changeTo',
      DELETE: 'videos.delete',
      REPLACE: 'videos.replace',
    },
    WEIGHT: 'itemWeight.set',
    WHEN_MADE: 'when_made.set',
    WHO_MADE: 'who_made.set',
    WIDTH: 'itemWidth.set',
  },
  [FAIRE]: {
    CASE_SIZE: 'unit_multiplier.set',
    CATEGORY: 'category_id.set',
    CONTINUE_SELLING: 'allow_sales_when_out_of_stock.set',
    DEADLINE_DATE: 'preorder_details_stop_selling_at.set',
    DESCRIPTION: 'description.set',
    DISTANCE_UNIT: 'measurements_distance_unit.set',
    END_DATE: 'preorder_details_expected_ship_window_end_date.set',
    GTIN: 'gtin.set',
    HEIGHT: 'measurements_height.set',
    KEEP_ACTIVE: 'preorder_details_keep_active_past_order_by_date.set',
    LENGTH: 'measurements_length.set',
    MASS_UNIT: 'measurements_mass_unit.set',
    MINIMUM_ORDER_QUANTITY: 'minimum_order_quantity.set',
    PER_STYLE_MINIMUM_ORDER_QUANTITY: 'per_style_minimum_order_quantity.set',
    PHOTOS: {
      ADD: 'photos.add',
      CHANGE_TO: 'photos.changeTo',
      DELETE: 'photos.delete',
      REPLACE: 'photos.replace',
      SWAP: 'photos.swap',
      UPDATE: 'photos.update',
    },
    PREORDER: 'preorderable.set',
    QUANTITY: 'available_quantity.set',
    RESTOCK_DATE: 'backordered_until.set',
    PRICES: 'prices.set',
    SCHEDULED_AT: 'scheduled_at.set',
    SIZE_UNIT: 'measurements_distance_unit.set',
    SKU: 'sku.set',
    START_DATE: 'preorder_details_expected_ship_date.set',
    STATUS: 'status.set',
    TARIFF_CODE: 'tariff_code.set',
    TITLE: 'title.set',
    VARIATIONS: 'variationsInventory.changeTo',
    WEIGHT: 'measurements_weight.set',
    WIDTH: 'measurements_width.set',
  },
  [SHOPIFY]: {
    BARCODE: 'barcodeInventory.changeTo',
    CAP: 'comapreAtPriceInventory.changeTo',
    CATEGORY: 'category.set',
    CHARGE_TAX: 'taxable.set',
    COLLECTIONS: 'collection_ids.set',
    CONTINUE_SELLING: 'inventoryPolicy.set',
    COUNTRY_CODE: 'countryCode.set',
    CPI: 'costInventory.changeTo',
    DESCRIPTION: 'body_html.set',
    HS_CODE: 'hsCode.set',
    KEYWORDS: 'keywords.set',
    META_DESCRIPTION: 'seoDescription.set',
    PAGE_TITLE: 'seoTitle.set',
    PHOTOS: {
      ADD: 'photos.add',
      CHANGE_TO: 'photos.changeTo',
      DELETE: 'photos.delete',
      REPLACE: 'photos.replace',
      SWAP: 'photos.swap',
      UPDATE: 'photos.update',
    },
    PHYSICAL: 'physical.set',
    PRICE: 'priceInventory.changeTo',
    PRODUCT_TYPE: 'product_type.set',
    QUANTITY: 'quantityInventory.changeTo',
    SCHEDULED_AT: 'scheduled_at.set',
    SKU: 'skuInventory.changeTo',
    STATUS: 'status.set',
    TAGS: {
      ADD: 'tags.add',
      DELETE: 'tags.delete',
      CHANGE_TO: 'tags.changeTo',
    },
    TITLE: 'title.set',
    TRACK_QUANTITY: 'inventoryManagement.set',
    VARIATIONS: 'variationsInventory.changeTo',
    VENDOR: 'vendor.set',
    VIDEOS: {
      ADD: 'videos.add',
      CHANGE_TO: 'videos.changeTo',
      DELETE: 'videos.delete',
      REPLACE: 'videos.replace',
    },
    WEIGHT: 'weight.changeTo',
  },
};

export const ATTRIBUTE_DEFAULTS = {
  [EBAY]: {
    [ATTRIBUTE.DESCRIPTION]: '',
    [ATTRIBUTE.PHOTOS]: EMPTY.LIST,
    [ATTRIBUTE.PRICE]: AMOUNT.ZERO_CENT,
    [ATTRIBUTE.QUANTITY]: AMOUNT.ZERO,
    [ATTRIBUTE.SKU]: '',
    [ATTRIBUTE.TITLE]: '',
    [ATTRIBUTE.VIDEOS]: EMPTY.LIST,
  },
  [ETSY]: {
    [ATTRIBUTE.DESCRIPTION]: '',
    [ATTRIBUTE.FILES]: EMPTY.LIST,
    [ATTRIBUTE.PHOTOS]: EMPTY.LIST,
    [ATTRIBUTE.PRICE]: AMOUNT.ZERO_CENT,
    [ATTRIBUTE.PROCESSING_PROFILE]: null,
    [ATTRIBUTE.QUANTITY]: AMOUNT.ONE,
    [ATTRIBUTE.SKU]: '',
    [ATTRIBUTE.TAGS]: EMPTY.ORDERED_MAP,
    [ATTRIBUTE.TITLE]: '',
    [ATTRIBUTE.VIDEOS]: EMPTY.LIST,
  },
  [FAIRE]: {
    [ATTRIBUTE.DEADLINE_DATE]: null,
    [ATTRIBUTE.DESCRIPTION]: '',
    [ATTRIBUTE.DISTANCE_UNIT]: SIZE_UNIT.IN,
    [ATTRIBUTE.END_DATE]: null,
    [ATTRIBUTE.GTIN]: '',
    [ATTRIBUTE.HEIGHT]: '',
    [ATTRIBUTE.INTERNATIONAL_PRICING]: false,
    [ATTRIBUTE.LENGTH]: '',
    [ATTRIBUTE.MASS_UNIT]: WEIGHT_UNIT.LB,
    [ATTRIBUTE.PHOTOS]: EMPTY.LIST,
    [ATTRIBUTE.PREORDER]: false,
    [ATTRIBUTE.QUANTITY]: '',
    [ATTRIBUTE.RESTOCK_DATE]: null,
    [ATTRIBUTE.RETAIL_PRICE]: '',
    [ATTRIBUTE.SKU]: '',
    [ATTRIBUTE.START_DATE]: null,
    [ATTRIBUTE.TARIFF_CODE]: '',
    [ATTRIBUTE.TITLE]: '',
    [ATTRIBUTE.WHOLESALE_PRICE]: '',
    [ATTRIBUTE.WEIGHT]: '',
    [ATTRIBUTE.WIDTH]: '',
  },
  [SHOPIFY]: {
    [ATTRIBUTE.BARCODE]: '',
    [ATTRIBUTE.CAP]: AMOUNT.ZERO_CENT,
    [ATTRIBUTE.CHARGE_TAX]: true,
    [ATTRIBUTE.COLLECTIONS]: EMPTY.LIST,
    [ATTRIBUTE.CONTINUE_SELLING]: true,
    [ATTRIBUTE.COUNTRY_CODE]: null,
    [ATTRIBUTE.CPI]: AMOUNT.ZERO_CENT,
    [ATTRIBUTE.DESCRIPTION]: '',
    [ATTRIBUTE.HS_CODE]: null,
    [ATTRIBUTE.PHOTOS]: EMPTY.LIST,
    [ATTRIBUTE.PHYSICAL]: false,
    [ATTRIBUTE.PRICE]: AMOUNT.ZERO_CENT,
    [ATTRIBUTE.QUANTITY]: AMOUNT.ZERO,
    [ATTRIBUTE.SKU]: '',
    [ATTRIBUTE.TAGS]: EMPTY.ORDERED_MAP,
    [ATTRIBUTE.TITLE]: '',
    [ATTRIBUTE.TRACK_QUANTITY]: false,
    [ATTRIBUTE.UNIT]: WEIGHT_UNIT.LB,
    [ATTRIBUTE.VIDEOS]: EMPTY.LIST,
    [ATTRIBUTE.WEIGHT]: AMOUNT.ZERO,
  },
};

export const DEFAULT_OPTION = {
  [SHOPIFY]: Map({
    shopify_name: 'Title',
    shopify_position: 1,
    shopify_values: List(['Default Title']),
  }),
};

export const DEFAULT_VARIANT = {
  [SHOPIFY]: Map({
    shopify_position: '1',
    shopify_grams: null,
    shopify_product_id: 0,
    shopify_sku: null,
    shopify_option1: 'Default Title',
    shopify_option2: null,
    shopify_option3: null,
    shopify_requires_shipping: true,
    shopify_inventory_quantity: AMOUNT.ZERO,
    shopify_barcode: null,
    shopify_taxable: true,
    shopify_inventory_policy: 'deny',
    shopify_price: AMOUNT.ZERO,
    shopify_compare_at_price: null,
    shopify_weight: AMOUNT.ZERO,
    shopify_weight_unit: WEIGHT_UNIT.LB,
    shopify_id: 0,
    shopify_image_id: null,
    deleted: false,
    id: '',
    shopify_harmonized_system_code: null,
    shopify_country_code_of_origin: null,
    shopify_cost: null,
    shopify_fulfillment_service: null,
    shopify_title: 'Default Title',
    shopify_old_inventory_quantity: '',
    shopify_inventory_management: SHOPIFY,
    shopify_admin_graphql_api_id: '',
  }),
};

export const EMPTY_VARIATIONS = {
  [EBAY]: Map({
    options: EMPTY.LIST,
    variants: EMPTY.LIST,
  }),
  [FAIRE]: Map({
    options: EMPTY.LIST,
    variants: EMPTY.LIST,
  }),
  [SHOPIFY]: Map({
    options: List([
      Map({
        shopify_name: '',
        shopify_position: '1',
        shopify_values: EMPTY.LIST,
      }),
      Map({
        shopify_name: '',
        shopify_position: '2',
        shopify_values: EMPTY.LIST,
      }),
      Map({
        shopify_name: '',
        shopify_position: '3',
        shopify_values: EMPTY.LIST,
      }),
    ]),
    variants: EMPTY.LIST,
  }),
};

export const ABOUT_TYPES = {
  [ETSY]: [
    ATTRIBUTE.WHO_MADE,
    ATTRIBUTE.IS_SUPPLY,
    ATTRIBUTE.WHEN_MADE,
  ],
};

export const ABOUT_VALUES = {
  [ETSY]: {
    [ATTRIBUTE.WHO_MADE]: {
      I_DID: 'i_did',
      COLLECTIVE: 'collective',
      SOMEONE_ELSE: 'someone_else',
    },
    [ATTRIBUTE.IS_SUPPLY]: {
      FINISHED_PRODUCT: false,
      SUPPLY_OR_TOOL: true,
    },
    [ATTRIBUTE.WHEN_MADE]: {
      MADE_TO_ORDER: 'made_to_order',
      '2020_2025': '2020_2025',
      '2010_2019': '2010_2019',
      '2006_2009': '2006_2009',
      BEFORE_2006: 'before_2006',
      '2000_2005': '2000_2005',
      '1990S': '1990s',
      '1980S': '1980s',
      '1970S': '1970s',
      '1960S': '1960s',
      '1950S': '1950s',
      '1940S': '1940s',
      '1930S': '1930s',
      '1920S': '1920s',
      '1910S': '1910s',
      '1900S': '1900s',
      '1800S': '1800s',
      '1700S': '1700s',
      BEFORE_1700: 'before_1700',
    },
  },
};

export const ABOUT_MAP = {
  [ETSY]: {
    [ATTRIBUTE.WHO_MADE]: {
      [ABOUT_VALUES[ETSY][ATTRIBUTE.WHO_MADE].I_DID]: 'I did',
      [ABOUT_VALUES[ETSY][ATTRIBUTE.WHO_MADE].COLLECTIVE]: 'A member of my shop',
      [ABOUT_VALUES[ETSY][ATTRIBUTE.WHO_MADE].SOMEONE_ELSE]: 'Another company or person',
    },
    [ATTRIBUTE.IS_SUPPLY]: {
      [ABOUT_VALUES[ETSY][ATTRIBUTE.IS_SUPPLY].SUPPLY_OR_TOOL]: 'A supply or tool to make things',
      [ABOUT_VALUES[ETSY][ATTRIBUTE.IS_SUPPLY].FINISHED_PRODUCT]: 'A finished product',
    },
    [ATTRIBUTE.WHEN_MADE]: {
      [ABOUT_VALUES[ETSY][ATTRIBUTE.WHEN_MADE].MADE_TO_ORDER]: 'Made To Order',
      [ABOUT_VALUES[ETSY][ATTRIBUTE.WHEN_MADE]['2020_2025']]: '2020 - 2025',
      [ABOUT_VALUES[ETSY][ATTRIBUTE.WHEN_MADE]['2010_2019']]: '2010 - 2019',
      [ABOUT_VALUES[ETSY][ATTRIBUTE.WHEN_MADE]['2006_2009']]: '2006 - 2009',
      [ABOUT_VALUES[ETSY][ATTRIBUTE.WHEN_MADE].BEFORE_2006]: 'Before 2006',
      [ABOUT_VALUES[ETSY][ATTRIBUTE.WHEN_MADE]['2000_2005']]: '2000 - 2005',
      [ABOUT_VALUES[ETSY][ATTRIBUTE.WHEN_MADE]['1900S']]: '1900 - 1909',
      [ABOUT_VALUES[ETSY][ATTRIBUTE.WHEN_MADE].BEFORE_1700]: 'Before 1700',
    },
  },
};

export const ABOUT_LIST = {
  [ETSY]: {
    [ATTRIBUTE.WHO_MADE]: Object.values(ABOUT_VALUES[ETSY][ATTRIBUTE.WHO_MADE]),
    [ATTRIBUTE.IS_SUPPLY]: Object.values(ABOUT_VALUES[ETSY][ATTRIBUTE.IS_SUPPLY]),
    [ATTRIBUTE.WHEN_MADE]: Object.values(ABOUT_VALUES[ETSY][ATTRIBUTE.WHEN_MADE]),
  },
};

export const ABOUT_OPERATIONS = {
  [ATTRIBUTE.WHO_MADE]: OPERATIONS[ETSY].WHO_MADE,
  [ATTRIBUTE.IS_SUPPLY]: OPERATIONS[ETSY].IS_SUPPLY,
  [ATTRIBUTE.WHEN_MADE]: OPERATIONS[ETSY].WHEN_MADE,
};

export const PRODUCTION_PARTNERS_TYPE = {
  OPTIONAL: 'optional',
  REQUIRED: 'required',
  UNAVAILABLE: 'unavailable',
};

export const SHIPPING_PROFILE = {
  FIXED: 'fixed',
  CALCULATED: 'calculated',
};

export const SHIPPING_PROFILE_MAP = {
  0: SHIPPING_PROFILE.FIXED,
  1: SHIPPING_PROFILE.CALCULATED,
};

export const SHIPPING_ERRORS = {
  [EBAY]: [
    ATTRIBUTE.COUNTRY,
    ATTRIBUTE.LOCATION,
    ATTRIBUTE.POSTAL_CODE,
    ATTRIBUTE.PAYMENT_POLICY,
    ATTRIBUTE.RETURN_POLICY,
    ATTRIBUTE.SHIPPING_POLICY,
    ATTRIBUTE.DEPTH,
    ATTRIBUTE.LENGTH,
    ATTRIBUTE.WIDTH,
    ATTRIBUTE.WEIGHT_MAJOR,
    ATTRIBUTE.WEIGHT_MINOR,
  ],
  [ETSY]: [
    ATTRIBUTE.PROCESSING_PROFILE,
    ATTRIBUTE.PROFILE_ID,
    ATTRIBUTE.RETURN_POLICY,
    ATTRIBUTE.HEIGHT,
    ATTRIBUTE.LENGTH,
    ATTRIBUTE.WEIGHT,
    ATTRIBUTE.WIDTH,
  ],
  [FAIRE]: [
    ATTRIBUTE.CASE_SIZE,
    ATTRIBUTE.MINIMUM_ORDER_QUANTITY,
    ATTRIBUTE.PER_STYLE_MINIMUM_ORDER_QUANTITY,
  ],
};

export const DIMENSIONS = {
  [EBAY]: [ATTRIBUTE.DEPTH, ATTRIBUTE.LENGTH, ATTRIBUTE.WIDTH],
  [ETSY]: [ATTRIBUTE.LENGTH, ATTRIBUTE.WIDTH, ATTRIBUTE.HEIGHT],
  [FAIRE]: [ATTRIBUTE.LENGTH, ATTRIBUTE.WIDTH, ATTRIBUTE.HEIGHT],
};

export const PERSONALIZATION_ERRORS = [ATTRIBUTE.CHAR_COUNT_MAX, ATTRIBUTE.INSTRUCTIONS];

export const TAXONOMY_ATTRIBUTE = {
  BOOL: 'bool',
  MULTI: 'multi',
  SCALED: 'scaled',
  SINGLE: 'single',
};

export const TAXONOMY_PROPERTY = {
  ALTER: 'alter',
  BOOL: 'bool',
  CUSTOM: 'custom',
  MULTI: 'multi',
  TEXT: 'text',
  SINGLE: 'single',
};

export const COLLECTION = {
  CUSTOM: 'custom',
  SMART: 'smart',
};

export const PRODUCT_ERRORS = {
  [EBAY]: [
    ATTRIBUTE.PHOTOS,
    ATTRIBUTE.TITLE,
    ATTRIBUTE.DESCRIPTION,
    ATTRIBUTE.ITEM_CATEGORY,
    ATTRIBUTE.CONDITION,
    ATTRIBUTE.ITEM_SPECIFICS,
    ATTRIBUTE.PRICE,
    ATTRIBUTE.PAYMENT_POLICY,
    ATTRIBUTE.QUANTITY,
    ATTRIBUTE.SKU,
    ATTRIBUTE.VARIATIONS,
    ATTRIBUTE.SHIPPING_POLICY,
    ATTRIBUTE.WEIGHT_MAJOR,
    ATTRIBUTE.WEIGHT_MINOR,
    ATTRIBUTE.DEPTH,
    ATTRIBUTE.LENGTH,
    ATTRIBUTE.WIDTH,
    ATTRIBUTE.COUNTRY,
    ATTRIBUTE.LOCATION,
    ATTRIBUTE.POSTAL_CODE,
    ATTRIBUTE.RETURN_POLICY,
  ],
  [ETSY]: [
    ATTRIBUTE.PHOTOS,
    ATTRIBUTE.TITLE,
    ATTRIBUTE.DESCRIPTION,
    ATTRIBUTE.TAGS,
    ATTRIBUTE.WHO_MADE,
    ATTRIBUTE.IS_SUPPLY,
    ATTRIBUTE.WHEN_MADE,
    ATTRIBUTE.CATEGORY,
    ATTRIBUTE.PRODUCTION_PARTNERS,
    ATTRIBUTE.TAXONOMY_ATTRIBUTES,
    ATTRIBUTE.PRICE,
    ATTRIBUTE.QUANTITY,
    ATTRIBUTE.SKU,
    ATTRIBUTE.VARIATIONS,
    ATTRIBUTE.INSTRUCTIONS,
    ATTRIBUTE.CHAR_COUNT_MAX,
    ATTRIBUTE.PROCESSING_PROFILE,
    ATTRIBUTE.PROFILE_ID,
    ATTRIBUTE.WEIGHT,
    ATTRIBUTE.LENGTH,
    ATTRIBUTE.WIDTH,
    ATTRIBUTE.HEIGHT,
    ATTRIBUTE.RETURN_POLICY,
    ATTRIBUTE.FILES,
  ],
  [FAIRE]: [
    ATTRIBUTE.PHOTOS,
    ATTRIBUTE.TITLE,
    ATTRIBUTE.DESCRIPTION,
    ATTRIBUTE.WHOLESALE_PRICE,
    ATTRIBUTE.RETAIL_PRICE,
    ATTRIBUTE.GTIN,
    ATTRIBUTE.VARIATIONS,
    ATTRIBUTE.CASE_SIZE,
    ATTRIBUTE.MINIMUM_ORDER_QUANTITY,
    ATTRIBUTE.PER_STYLE_MINIMUM_ORDER_QUANTITY,
    ATTRIBUTE.WEIGHT,
    ATTRIBUTE.LENGTH,
    ATTRIBUTE.WIDTH,
    ATTRIBUTE.HEIGHT,
  ],
  [SHOPIFY]: [
    ATTRIBUTE.PHOTOS,
    ATTRIBUTE.TITLE,
    ATTRIBUTE.DESCRIPTION,
    ATTRIBUTE.TAGS,
    ATTRIBUTE.PRICE,
    ATTRIBUTE.CAP,
    ATTRIBUTE.CPI,
    ATTRIBUTE.QUANTITY,
    ATTRIBUTE.SKU,
    ATTRIBUTE.BARCODE,
    ATTRIBUTE.VARIATIONS,
    ATTRIBUTE.WEIGHT,
  ],
};

export const NOT_AVAILABLE = 'NA';

export const GRADES = ['F-', 'F', 'F+', 'D-', 'D', 'D+', 'C-', 'C\u2009', 'C+', 'B-', 'B', 'B+', 'A-', 'A', 'A+'];

export const SCORE_DETAILS = {
  [ETSY]: [
    ATTRIBUTE.TITLE,
    ATTRIBUTE.DESCRIPTION,
    ATTRIBUTE.TAGS,
  ],
  [SHOPIFY]: [
    ATTRIBUTE.TITLE,
    ATTRIBUTE.DESCRIPTION,
    ATTRIBUTE.PAGE_TITLE,
    ATTRIBUTE.META_DESCRIPTION,
  ],
  [EBAY]: [
    ATTRIBUTE.TITLE,
    ATTRIBUTE.DESCRIPTION,
  ],
  [FAIRE]: [
    ATTRIBUTE.TITLE,
    ATTRIBUTE.DESCRIPTION,
  ],
};

export const SCORE_ATTRIBUTE_LABEL = {
  [ATTRIBUTE.DESCRIPTION]: 'Description',
  [ATTRIBUTE.TAGS]: 'Tags',
  [ATTRIBUTE.TITLE]: 'Title',
  [ATTRIBUTE.PAGE_TITLE]: 'Page title',
  [ATTRIBUTE.META_DESCRIPTION]: 'Meta description',
};

export const COPY_FLOW = {
  CREATE_SHOP: 'create-shop',
  COPY_LISTINGS: 'copy-listings',
};

// https://developer.ebay.com/devzone/xml/docs/reference/ebay/types/CountryCodeType.html
export const COUNTRY_CODE = {
  AA: 'AA',
  AD: 'AD',
  AE: 'AE',
  AF: 'AF',
  AG: 'AG',
  AI: 'AI',
  AL: 'AL',
  AM: 'AM',
  AN: 'AN',
  AO: 'AO',
  AQ: 'AQ',
  AR: 'AR',
  AS: 'AS',
  AT: 'AT',
  AU: 'AU',
  AW: 'AW',
  AZ: 'AZ',
  BA: 'BA',
  BB: 'BB',
  BD: 'BD',
  BE: 'BE',
  BF: 'BF',
  BG: 'BG',
  BH: 'BH',
  BI: 'BI',
  BJ: 'BJ',
  BM: 'BM',
  BN: 'BN',
  BO: 'BO',
  BR: 'BR',
  BS: 'BS',
  BT: 'BT',
  BV: 'BV',
  BW: 'BW',
  BY: 'BY',
  BZ: 'BZ',
  CA: 'CA',
  CC: 'CC',
  CD: 'CD',
  CF: 'CF',
  CG: 'CG',
  CH: 'CH',
  CI: 'CI',
  CK: 'CK',
  CL: 'CL',
  CM: 'CM',
  CN: 'CN',
  CO: 'CO',
  CR: 'CR',
  CU: 'CU',
  CV: 'CV',
  CX: 'CX',
  CY: 'CY',
  CZ: 'CZ',
  DE: 'DE',
  DJ: 'DJ',
  DK: 'DK',
  DM: 'DM',
  DO: 'DO',
  DZ: 'DZ',
  EC: 'EC',
  EE: 'EE',
  EG: 'EG',
  EH: 'EH',
  ER: 'ER',
  ES: 'ES',
  ET: 'ET',
  FI: 'FI',
  FJ: 'FJ',
  FK: 'FK',
  FM: 'FM',
  FO: 'FO',
  FR: 'FR',
  GA: 'GA',
  GB: 'GB',
  GD: 'GD',
  GE: 'GE',
  GF: 'GF',
  GG: 'GG',
  GH: 'GH',
  GI: 'GI',
  GL: 'GL',
  GM: 'GM',
  GN: 'GN',
  GP: 'GP',
  GQ: 'GQ',
  GR: 'GR',
  GS: 'GS',
  GT: 'GT',
  GU: 'GU',
  GW: 'GW',
  GY: 'GY',
  HK: 'HK',
  HM: 'HM',
  HN: 'HN',
  HR: 'HR',
  HT: 'HT',
  HU: 'HU',
  ID: 'ID',
  IE: 'IE',
  IL: 'IL',
  IN: 'IN',
  IO: 'IO',
  IQ: 'IQ',
  IR: 'IR',
  IS: 'IS',
  IT: 'IT',
  JE: 'JE',
  JM: 'JM',
  JO: 'JO',
  JP: 'JP',
  KE: 'KE',
  KG: 'KG',
  KH: 'KH',
  KI: 'KI',
  KM: 'KM',
  KN: 'KN',
  KP: 'KP',
  KR: 'KR',
  KW: 'KW',
  KY: 'KY',
  KZ: 'KZ',
  LA: 'LA',
  LB: 'LB',
  LC: 'LC',
  LI: 'LI',
  LK: 'LK',
  LR: 'LR',
  LS: 'LS',
  LT: 'LT',
  LU: 'LU',
  LV: 'LV',
  LY: 'LY',
  MA: 'MA',
  MC: 'MC',
  MD: 'MD',
  ME: 'ME',
  MG: 'MG',
  MH: 'MH',
  MK: 'MK',
  ML: 'ML',
  MM: 'MM',
  MN: 'MN',
  MO: 'MO',
  MP: 'MP',
  MQ: 'MQ',
  MR: 'MR',
  MS: 'MS',
  MT: 'MT',
  MU: 'MU',
  MV: 'MV',
  MW: 'MW',
  MX: 'MX',
  MY: 'MY',
  MZ: 'MZ',
  NA: 'NA',
  NC: 'NC',
  NE: 'NE',
  NF: 'NF',
  NG: 'NG',
  NI: 'NI',
  NL: 'NL',
  NO: 'NO',
  NP: 'NP',
  NR: 'NR',
  NU: 'NU',
  NZ: 'NZ',
  OM: 'OM',
  PA: 'PA',
  PE: 'PE',
  PF: 'PF',
  PG: 'PG',
  PH: 'PH',
  PK: 'PK',
  PL: 'PL',
  PM: 'PM',
  PN: 'PN',
  PR: 'PR',
  PS: 'PS',
  PT: 'PT',
  PW: 'PW',
  PY: 'PY',
  QA: 'QA',
  QM: 'QM',
  QN: 'QN',
  QO: 'QO',
  RE: 'RE',
  RO: 'RO',
  RS: 'RS',
  RU: 'RU',
  RW: 'RW',
  SA: 'SA',
  SB: 'SB',
  SC: 'SC',
  SD: 'SD',
  SE: 'SE',
  SG: 'SG',
  SH: 'SH',
  SI: 'SI',
  SJ: 'SJ',
  SK: 'SK',
  SL: 'SL',
  SM: 'SM',
  SN: 'SN',
  SO: 'SO',
  SR: 'SR',
  ST: 'ST',
  SV: 'SV',
  SY: 'SY',
  SZ: 'SZ',
  TC: 'TC',
  TD: 'TD',
  TF: 'TF',
  TG: 'TG',
  TH: 'TH',
  TJ: 'TJ',
  TK: 'TK',
  TM: 'TM',
  TN: 'TN',
  TO: 'TO',
  TR: 'TR',
  TT: 'TT',
  TV: 'TV',
  TW: 'TW',
  TZ: 'TZ',
  UA: 'UA',
  UG: 'UG',
  UM: 'UM',
  US: 'US',
  UY: 'UY',
  UZ: 'UZ',
  VA: 'VA',
  VC: 'VC',
  VE: 'VE',
  VG: 'VG',
  VI: 'VI',
  VN: 'VN',
  VU: 'VU',
  WF: 'WF',
  WS: 'WS',
  YE: 'YE',
  YT: 'YT',
  YU: 'YU',
  ZA: 'ZA',
  ZM: 'ZM',
  ZW: 'ZW',
  ZZ: 'ZZ',
};

export const COUNTRY_BY_CODE = {
  [COUNTRY_CODE.AA]: 'APO/FPO',
  [COUNTRY_CODE.AD]: 'Andorra',
  [COUNTRY_CODE.AE]: 'United Arab Emirates',
  [COUNTRY_CODE.AF]: 'Afghanistan',
  [COUNTRY_CODE.AG]: 'Antigua and Barbuda',
  [COUNTRY_CODE.AI]: 'Anguilla',
  [COUNTRY_CODE.AL]: 'Albania',
  [COUNTRY_CODE.AM]: 'Armenia',
  [COUNTRY_CODE.AN]: 'Netherlands Antilles',
  [COUNTRY_CODE.AO]: 'Angola',
  [COUNTRY_CODE.AQ]: 'Antarctica',
  [COUNTRY_CODE.AR]: 'Argentina',
  [COUNTRY_CODE.AS]: 'American Samoa',
  [COUNTRY_CODE.AT]: 'Austria',
  [COUNTRY_CODE.AU]: 'Australia',
  [COUNTRY_CODE.AW]: 'Aruba',
  [COUNTRY_CODE.AZ]: 'Azerbaijan',
  [COUNTRY_CODE.BA]: 'Bosnia and Herzegovina',
  [COUNTRY_CODE.BB]: 'Barbados',
  [COUNTRY_CODE.BD]: 'Bangladesh',
  [COUNTRY_CODE.BE]: 'Belgium',
  [COUNTRY_CODE.BF]: 'Burkina Faso',
  [COUNTRY_CODE.BG]: 'Bulgaria',
  [COUNTRY_CODE.BH]: 'Bahrain',
  [COUNTRY_CODE.BI]: 'Burundi',
  [COUNTRY_CODE.BJ]: 'Benin',
  [COUNTRY_CODE.BM]: 'Bermuda',
  [COUNTRY_CODE.BN]: 'Brunei Darussalam',
  [COUNTRY_CODE.BO]: 'Bolivia',
  [COUNTRY_CODE.BR]: 'Brazil',
  [COUNTRY_CODE.BS]: 'Bahamas',
  [COUNTRY_CODE.BT]: 'Bhutan',
  [COUNTRY_CODE.BV]: 'Bouvet Island',
  [COUNTRY_CODE.BW]: 'Botswana',
  [COUNTRY_CODE.BY]: 'Belarus',
  [COUNTRY_CODE.BZ]: 'Belize',
  [COUNTRY_CODE.CA]: 'Canada',
  [COUNTRY_CODE.CC]: 'Cocos (Keeling) Islands',
  [COUNTRY_CODE.CD]: 'Congo, The Democratic Republic of the',
  [COUNTRY_CODE.CF]: 'Central African Republic',
  [COUNTRY_CODE.CG]: 'Congo',
  [COUNTRY_CODE.CH]: 'Switzerland',
  [COUNTRY_CODE.CI]: 'Cote d\'Ivoire',
  [COUNTRY_CODE.CK]: 'Cook Islands',
  [COUNTRY_CODE.CL]: 'Chile',
  [COUNTRY_CODE.CM]: 'Cameroon',
  [COUNTRY_CODE.CN]: 'China',
  [COUNTRY_CODE.CO]: 'Colombia',
  [COUNTRY_CODE.CR]: 'Costa Rica',
  [COUNTRY_CODE.CU]: 'Cuba',
  [COUNTRY_CODE.CV]: 'Cape Verde',
  [COUNTRY_CODE.CX]: 'Christmas Island',
  [COUNTRY_CODE.CY]: 'Cyprus',
  [COUNTRY_CODE.CZ]: 'Czech Republic',
  [COUNTRY_CODE.DE]: 'Germany',
  [COUNTRY_CODE.DJ]: 'Djibouti',
  [COUNTRY_CODE.DK]: 'Denmark',
  [COUNTRY_CODE.DM]: 'Dominica',
  [COUNTRY_CODE.DO]: 'Dominican Republic',
  [COUNTRY_CODE.DZ]: 'Algeria',
  [COUNTRY_CODE.EC]: 'Ecuador',
  [COUNTRY_CODE.EE]: 'Estonia',
  [COUNTRY_CODE.EG]: 'Egypt',
  [COUNTRY_CODE.EH]: 'Western Sahara',
  [COUNTRY_CODE.ER]: 'Eritrea',
  [COUNTRY_CODE.ES]: 'Spain',
  [COUNTRY_CODE.ET]: 'Ethiopia',
  [COUNTRY_CODE.FI]: 'Finland',
  [COUNTRY_CODE.FJ]: 'Fiji',
  [COUNTRY_CODE.FK]: 'Falkland Islands (Malvinas)',
  [COUNTRY_CODE.FM]: 'Federated States of Micronesia',
  [COUNTRY_CODE.FO]: 'Faroe Islands',
  [COUNTRY_CODE.FR]: 'France',
  [COUNTRY_CODE.GA]: 'Gabon',
  [COUNTRY_CODE.GB]: 'United Kingdom',
  [COUNTRY_CODE.GD]: 'Grenada',
  [COUNTRY_CODE.GE]: 'Georgia',
  [COUNTRY_CODE.GF]: 'French Guiana',
  [COUNTRY_CODE.GG]: 'Guernse',
  [COUNTRY_CODE.GH]: 'Ghana',
  [COUNTRY_CODE.GI]: 'Gibraltar',
  [COUNTRY_CODE.GL]: 'Greenland',
  [COUNTRY_CODE.GM]: 'Gambia',
  [COUNTRY_CODE.GN]: 'Guinea',
  [COUNTRY_CODE.GP]: 'Guadeloupe',
  [COUNTRY_CODE.GQ]: 'Equatorial Guinea',
  [COUNTRY_CODE.GR]: 'Greece',
  [COUNTRY_CODE.GS]: 'South Georgia and the South Sandwich Islands',
  [COUNTRY_CODE.GT]: 'Guatemala',
  [COUNTRY_CODE.GU]: 'Guam',
  [COUNTRY_CODE.GW]: 'Guinea-Bissau',
  [COUNTRY_CODE.GY]: 'Guyana',
  [COUNTRY_CODE.HK]: 'Hong Kong',
  [COUNTRY_CODE.HM]: 'Heard Island and McDonald Islands',
  [COUNTRY_CODE.HN]: 'Honduras',
  [COUNTRY_CODE.HR]: 'Croatia',
  [COUNTRY_CODE.HT]: 'Haiti',
  [COUNTRY_CODE.HU]: 'Hungary',
  [COUNTRY_CODE.ID]: 'Indonesia',
  [COUNTRY_CODE.IE]: 'Ireland',
  [COUNTRY_CODE.IL]: 'Israel',
  [COUNTRY_CODE.IN]: 'India',
  [COUNTRY_CODE.IO]: 'British Indian Ocean Territory',
  [COUNTRY_CODE.IQ]: 'Iraq',
  [COUNTRY_CODE.IR]: 'Islamic Republic of Iran',
  [COUNTRY_CODE.IS]: 'Iceland',
  [COUNTRY_CODE.IT]: 'Italy',
  [COUNTRY_CODE.JE]: 'Jerse',
  [COUNTRY_CODE.JM]: 'Jamaica',
  [COUNTRY_CODE.JO]: 'Jordan',
  [COUNTRY_CODE.JP]: 'Japan',
  [COUNTRY_CODE.KE]: 'Kenya',
  [COUNTRY_CODE.KG]: 'Kyrgyzstan',
  [COUNTRY_CODE.KH]: 'Cambodia',
  [COUNTRY_CODE.KI]: 'Kiribati',
  [COUNTRY_CODE.KM]: 'Comoros',
  [COUNTRY_CODE.KN]: 'Saint Kitts and Nevis',
  [COUNTRY_CODE.KP]: 'Democratic People\'s Republic of Korea',
  [COUNTRY_CODE.KR]: 'Republic of Korea',
  [COUNTRY_CODE.KW]: 'Kuwait',
  [COUNTRY_CODE.KY]: 'Cayman Islands',
  [COUNTRY_CODE.KZ]: 'Kazakhstan',
  [COUNTRY_CODE.LA]: 'Lao People\'s Democratic Republic',
  [COUNTRY_CODE.LB]: 'Lebanon',
  [COUNTRY_CODE.LC]: 'Saint Lucia',
  [COUNTRY_CODE.LI]: 'Liechtenstein',
  [COUNTRY_CODE.LK]: 'Sri Lanka',
  [COUNTRY_CODE.LR]: 'Liberia',
  [COUNTRY_CODE.LS]: 'Lesotho',
  [COUNTRY_CODE.LT]: 'Lithuania',
  [COUNTRY_CODE.LU]: 'Luxembourg',
  [COUNTRY_CODE.LV]: 'Latvia',
  [COUNTRY_CODE.LY]: 'Libyan Arab Jamahiriya',
  [COUNTRY_CODE.MA]: 'Morocco',
  [COUNTRY_CODE.MC]: 'Monaco',
  [COUNTRY_CODE.MD]: 'Republic of Moldova',
  [COUNTRY_CODE.ME]: 'Montenegro',
  [COUNTRY_CODE.MG]: 'Madagascar',
  [COUNTRY_CODE.MH]: 'Marshall Islands',
  [COUNTRY_CODE.MK]: 'The Former Yugoslav Republic of Macedonia',
  [COUNTRY_CODE.ML]: 'Mali',
  [COUNTRY_CODE.MM]: 'Myanmar',
  [COUNTRY_CODE.MN]: 'Mongolia',
  [COUNTRY_CODE.MO]: 'Macao',
  [COUNTRY_CODE.MP]: 'Northern Mariana Islands',
  [COUNTRY_CODE.MQ]: 'Martinique',
  [COUNTRY_CODE.MR]: 'Mauritania',
  [COUNTRY_CODE.MS]: 'Montserrat',
  [COUNTRY_CODE.MT]: 'Malta',
  [COUNTRY_CODE.MU]: 'Mauritius',
  [COUNTRY_CODE.MV]: 'Maldives',
  [COUNTRY_CODE.MW]: 'Malawi',
  [COUNTRY_CODE.MX]: 'Mexico',
  [COUNTRY_CODE.MY]: 'Malaysia',
  [COUNTRY_CODE.MZ]: 'Mozambique',
  [COUNTRY_CODE.NA]: 'Namibia',
  [COUNTRY_CODE.NC]: 'New Caledonia',
  [COUNTRY_CODE.NE]: 'Niger',
  [COUNTRY_CODE.NF]: 'Norfolk Island',
  [COUNTRY_CODE.NG]: 'Nigeria',
  [COUNTRY_CODE.NI]: 'Nicaragua',
  [COUNTRY_CODE.NL]: 'Netherlands',
  [COUNTRY_CODE.NO]: 'Norway',
  [COUNTRY_CODE.NP]: 'Nepal',
  [COUNTRY_CODE.NR]: 'Nauru',
  [COUNTRY_CODE.NU]: 'Niue',
  [COUNTRY_CODE.NZ]: 'New Zealand',
  [COUNTRY_CODE.OM]: 'Oman',
  [COUNTRY_CODE.PA]: 'Panama',
  [COUNTRY_CODE.PE]: 'Peru',
  [COUNTRY_CODE.PF]: 'French Polynesia. Includes Tahiti',
  [COUNTRY_CODE.PG]: 'Papua New Guinea',
  [COUNTRY_CODE.PH]: 'Philippines',
  [COUNTRY_CODE.PK]: 'Pakistan',
  [COUNTRY_CODE.PL]: 'Poland',
  [COUNTRY_CODE.PM]: 'Saint Pierre and Miquelon',
  [COUNTRY_CODE.PN]: 'Pitcairn',
  [COUNTRY_CODE.PR]: 'Puerto Rico',
  [COUNTRY_CODE.PS]: 'Palestinian territory, Occupied',
  [COUNTRY_CODE.PT]: 'Portugal',
  [COUNTRY_CODE.PW]: 'Palau',
  [COUNTRY_CODE.PY]: 'Paraguay',
  [COUNTRY_CODE.QA]: 'Qatar',
  [COUNTRY_CODE.QM]: 'Guernsey',
  [COUNTRY_CODE.QN]: 'Jan Mayen',
  [COUNTRY_CODE.QO]: 'Jersey',
  [COUNTRY_CODE.RE]: 'Reunion',
  [COUNTRY_CODE.RO]: 'Romania',
  [COUNTRY_CODE.RS]: 'Serbi',
  [COUNTRY_CODE.RU]: 'Russian Federation',
  [COUNTRY_CODE.RW]: 'Rwanda',
  [COUNTRY_CODE.SA]: 'Saudi Arabia',
  [COUNTRY_CODE.SB]: 'Solomon Islands',
  [COUNTRY_CODE.SC]: 'Seychelles',
  [COUNTRY_CODE.SD]: 'Sudan',
  [COUNTRY_CODE.SE]: 'Sweden',
  [COUNTRY_CODE.SG]: 'Singapore',
  [COUNTRY_CODE.SH]: 'Saint Helena',
  [COUNTRY_CODE.SI]: 'Slovenia',
  [COUNTRY_CODE.SJ]: 'Svalbard and Jan Mayen',
  [COUNTRY_CODE.SK]: 'Slovakia',
  [COUNTRY_CODE.SL]: 'Sierra Leone',
  [COUNTRY_CODE.SM]: 'San Marino',
  [COUNTRY_CODE.SN]: 'Senegal',
  [COUNTRY_CODE.SO]: 'Somalia',
  [COUNTRY_CODE.SR]: 'Suriname',
  [COUNTRY_CODE.ST]: 'Sao Tome and Principe',
  [COUNTRY_CODE.SV]: 'El Salvador',
  [COUNTRY_CODE.SY]: 'Syrian Arab Republic',
  [COUNTRY_CODE.SZ]: 'Swaziland',
  [COUNTRY_CODE.TC]: 'Turks and Caicos Islands',
  [COUNTRY_CODE.TD]: 'Chad',
  [COUNTRY_CODE.TF]: 'French Southern Territories',
  [COUNTRY_CODE.TG]: 'Togo',
  [COUNTRY_CODE.TH]: 'Thailand',
  [COUNTRY_CODE.TJ]: 'Tajikistan',
  [COUNTRY_CODE.TK]: 'Tokelau',
  [COUNTRY_CODE.TM]: 'Turkmenistan',
  [COUNTRY_CODE.TN]: 'Tunisia',
  [COUNTRY_CODE.TO]: 'Tonga',
  [COUNTRY_CODE.TR]: 'Turkey',
  [COUNTRY_CODE.TT]: 'Trinidad and Tobago',
  [COUNTRY_CODE.TV]: 'Tuvalu',
  [COUNTRY_CODE.TW]: 'Taiwan, Province of China',
  [COUNTRY_CODE.TZ]: 'Tanzania, United Republic of',
  [COUNTRY_CODE.UA]: 'Ukraine',
  [COUNTRY_CODE.UG]: 'Uganda',
  [COUNTRY_CODE.UM]: 'United States Minor Outlying Islands',
  [COUNTRY_CODE.US]: 'United States',
  [COUNTRY_CODE.UY]: 'Uruguay',
  [COUNTRY_CODE.UZ]: 'Uzbekistan',
  [COUNTRY_CODE.VA]: 'Holy See (Vatican City state)',
  [COUNTRY_CODE.VC]: 'Saint Vincent and the Grenadines',
  [COUNTRY_CODE.VE]: 'Venezuela',
  [COUNTRY_CODE.VG]: 'Virgin Islands, British',
  [COUNTRY_CODE.VI]: 'Virgin Islands, U.S',
  [COUNTRY_CODE.VN]: 'Vietnam',
  [COUNTRY_CODE.VU]: 'Vanuatu',
  [COUNTRY_CODE.WF]: 'Wallis and Futuna',
  [COUNTRY_CODE.WS]: 'Samoa',
  [COUNTRY_CODE.YE]: 'Yemen',
  [COUNTRY_CODE.YT]: 'Mayotte',
  [COUNTRY_CODE.YU]: 'Yugoslavia',
  [COUNTRY_CODE.ZA]: 'South Africa',
  [COUNTRY_CODE.ZM]: 'Zambia',
  [COUNTRY_CODE.ZW]: 'Zimbabwe',
  [COUNTRY_CODE.ZZ]: 'Unknown country',
};

export const COUNTRY_CODES = [
  COUNTRY_CODE.AF,
  COUNTRY_CODE.AL,
  COUNTRY_CODE.DZ,
  COUNTRY_CODE.AS,
  COUNTRY_CODE.AD,
  COUNTRY_CODE.AO,
  COUNTRY_CODE.AI,
  COUNTRY_CODE.AQ,
  COUNTRY_CODE.AG,
  COUNTRY_CODE.AR,
  COUNTRY_CODE.AM,
  COUNTRY_CODE.AW,
  COUNTRY_CODE.AU,
  COUNTRY_CODE.AT,
  COUNTRY_CODE.AZ,
  COUNTRY_CODE.BS,
  COUNTRY_CODE.BH,
  COUNTRY_CODE.BD,
  COUNTRY_CODE.BB,
  COUNTRY_CODE.BY,
  COUNTRY_CODE.BE,
  COUNTRY_CODE.BZ,
  COUNTRY_CODE.BJ,
  COUNTRY_CODE.BM,
  COUNTRY_CODE.BT,
  COUNTRY_CODE.BO,
  COUNTRY_CODE.BA,
  COUNTRY_CODE.BW,
  COUNTRY_CODE.BV,
  COUNTRY_CODE.BR,
  COUNTRY_CODE.IO,
  COUNTRY_CODE.BN,
  COUNTRY_CODE.BG,
  COUNTRY_CODE.BF,
  COUNTRY_CODE.BI,
  COUNTRY_CODE.KH,
  COUNTRY_CODE.CM,
  COUNTRY_CODE.CA,
  COUNTRY_CODE.CV,
  COUNTRY_CODE.KY,
  COUNTRY_CODE.CF,
  COUNTRY_CODE.TD,
  COUNTRY_CODE.CL,
  COUNTRY_CODE.CN,
  COUNTRY_CODE.CX,
  COUNTRY_CODE.CC,
  COUNTRY_CODE.CO,
  COUNTRY_CODE.KM,
  COUNTRY_CODE.CG,
  COUNTRY_CODE.CD,
  COUNTRY_CODE.CK,
  COUNTRY_CODE.CR,
  COUNTRY_CODE.CI,
  COUNTRY_CODE.HR,
  COUNTRY_CODE.CU,
  COUNTRY_CODE.CY,
  COUNTRY_CODE.CZ,
  COUNTRY_CODE.KP,
  COUNTRY_CODE.DK,
  COUNTRY_CODE.DJ,
  COUNTRY_CODE.DM,
  COUNTRY_CODE.DO,
  COUNTRY_CODE.EC,
  COUNTRY_CODE.EG,
  COUNTRY_CODE.SV,
  COUNTRY_CODE.GQ,
  COUNTRY_CODE.ER,
  COUNTRY_CODE.EE,
  COUNTRY_CODE.ET,
  COUNTRY_CODE.FK,
  COUNTRY_CODE.FO,
  COUNTRY_CODE.FM,
  COUNTRY_CODE.FJ,
  COUNTRY_CODE.FI,
  COUNTRY_CODE.FR,
  COUNTRY_CODE.GF,
  COUNTRY_CODE.PF,
  COUNTRY_CODE.TF,
  COUNTRY_CODE.GA,
  COUNTRY_CODE.GM,
  COUNTRY_CODE.GE,
  COUNTRY_CODE.DE,
  COUNTRY_CODE.GH,
  COUNTRY_CODE.GI,
  COUNTRY_CODE.GR,
  COUNTRY_CODE.GL,
  COUNTRY_CODE.GD,
  COUNTRY_CODE.GP,
  COUNTRY_CODE.GU,
  COUNTRY_CODE.GT,
  COUNTRY_CODE.GG,
  COUNTRY_CODE.GN,
  COUNTRY_CODE.GW,
  COUNTRY_CODE.GY,
  COUNTRY_CODE.HT,
  COUNTRY_CODE.HM,
  COUNTRY_CODE.VA,
  COUNTRY_CODE.HN,
  COUNTRY_CODE.HK,
  COUNTRY_CODE.HU,
  COUNTRY_CODE.IS,
  COUNTRY_CODE.IN,
  COUNTRY_CODE.ID,
  COUNTRY_CODE.IQ,
  COUNTRY_CODE.IE,
  COUNTRY_CODE.IR,
  COUNTRY_CODE.IL,
  COUNTRY_CODE.IT,
  COUNTRY_CODE.JM,
  COUNTRY_CODE.JP,
  COUNTRY_CODE.JE,
  COUNTRY_CODE.JO,
  COUNTRY_CODE.KZ,
  COUNTRY_CODE.KE,
  COUNTRY_CODE.KI,
  COUNTRY_CODE.KW,
  COUNTRY_CODE.KG,
  COUNTRY_CODE.LA,
  COUNTRY_CODE.LV,
  COUNTRY_CODE.LB,
  COUNTRY_CODE.LS,
  COUNTRY_CODE.LR,
  COUNTRY_CODE.LY,
  COUNTRY_CODE.LI,
  COUNTRY_CODE.LT,
  COUNTRY_CODE.LU,
  COUNTRY_CODE.MO,
  COUNTRY_CODE.MG,
  COUNTRY_CODE.MW,
  COUNTRY_CODE.MY,
  COUNTRY_CODE.MV,
  COUNTRY_CODE.ML,
  COUNTRY_CODE.MT,
  COUNTRY_CODE.MH,
  COUNTRY_CODE.MQ,
  COUNTRY_CODE.MR,
  COUNTRY_CODE.MU,
  COUNTRY_CODE.YT,
  COUNTRY_CODE.MX,
  COUNTRY_CODE.MC,
  COUNTRY_CODE.MN,
  COUNTRY_CODE.ME,
  COUNTRY_CODE.MS,
  COUNTRY_CODE.MA,
  COUNTRY_CODE.MZ,
  COUNTRY_CODE.MM,
  COUNTRY_CODE.NA,
  COUNTRY_CODE.NR,
  COUNTRY_CODE.NP,
  COUNTRY_CODE.NL,
  COUNTRY_CODE.AN,
  COUNTRY_CODE.NC,
  COUNTRY_CODE.NZ,
  COUNTRY_CODE.NI,
  COUNTRY_CODE.NE,
  COUNTRY_CODE.NG,
  COUNTRY_CODE.NU,
  COUNTRY_CODE.NF,
  COUNTRY_CODE.MP,
  COUNTRY_CODE.NO,
  COUNTRY_CODE.OM,
  COUNTRY_CODE.PK,
  COUNTRY_CODE.PW,
  COUNTRY_CODE.PS,
  COUNTRY_CODE.PA,
  COUNTRY_CODE.PG,
  COUNTRY_CODE.PY,
  COUNTRY_CODE.PE,
  COUNTRY_CODE.PH,
  COUNTRY_CODE.PN,
  COUNTRY_CODE.PL,
  COUNTRY_CODE.PT,
  COUNTRY_CODE.PR,
  COUNTRY_CODE.QA,
  COUNTRY_CODE.KR,
  COUNTRY_CODE.MD,
  COUNTRY_CODE.RE,
  COUNTRY_CODE.RO,
  COUNTRY_CODE.RU,
  COUNTRY_CODE.RW,
  COUNTRY_CODE.SH,
  COUNTRY_CODE.KN,
  COUNTRY_CODE.LC,
  COUNTRY_CODE.PM,
  COUNTRY_CODE.VC,
  COUNTRY_CODE.WS,
  COUNTRY_CODE.SM,
  COUNTRY_CODE.ST,
  COUNTRY_CODE.SA,
  COUNTRY_CODE.SN,
  COUNTRY_CODE.RS,
  COUNTRY_CODE.SC,
  COUNTRY_CODE.SL,
  COUNTRY_CODE.SG,
  COUNTRY_CODE.SK,
  COUNTRY_CODE.SI,
  COUNTRY_CODE.SB,
  COUNTRY_CODE.SO,
  COUNTRY_CODE.ZA,
  COUNTRY_CODE.GS,
  COUNTRY_CODE.ES,
  COUNTRY_CODE.LK,
  COUNTRY_CODE.SD,
  COUNTRY_CODE.SR,
  COUNTRY_CODE.SJ,
  COUNTRY_CODE.SZ,
  COUNTRY_CODE.SE,
  COUNTRY_CODE.CH,
  COUNTRY_CODE.SY,
  COUNTRY_CODE.TW,
  COUNTRY_CODE.TJ,
  COUNTRY_CODE.TZ,
  COUNTRY_CODE.TH,
  COUNTRY_CODE.MK,
  COUNTRY_CODE.TG,
  COUNTRY_CODE.TK,
  COUNTRY_CODE.TO,
  COUNTRY_CODE.TT,
  COUNTRY_CODE.TN,
  COUNTRY_CODE.TR,
  COUNTRY_CODE.TM,
  COUNTRY_CODE.TC,
  COUNTRY_CODE.TV,
  COUNTRY_CODE.UG,
  COUNTRY_CODE.UA,
  COUNTRY_CODE.AE,
  COUNTRY_CODE.GB,
  COUNTRY_CODE.US,
  COUNTRY_CODE.UY,
  COUNTRY_CODE.UZ,
  COUNTRY_CODE.VU,
  COUNTRY_CODE.VE,
  COUNTRY_CODE.VN,
  COUNTRY_CODE.VG,
  COUNTRY_CODE.VI,
  COUNTRY_CODE.WF,
  COUNTRY_CODE.EH,
  COUNTRY_CODE.YE,
  COUNTRY_CODE.ZM,
  COUNTRY_CODE.ZW,
];
